/**
 * Pick which chase binder cards are going into.
 *
 * The visual is the collection's BinderPickerSheet, deliberately, so the two
 * feel like one app. The data is not: that sheet counts cards already sitting
 * in a binder through cards.binder_id, and a chase binder holds cards nobody
 * owns yet, so the counts here come from wish_binder_cards.
 */

import { Folder, FolderPlus, X } from 'lucide-react';
import BottomSheet from '../BottomSheet';
import { useTheme } from '../../context/ThemeContext';
import haptics from '../../utils/haptics';

export default function WishBinderPicker({
  isOpen,
  binders = [],
  counts = {},
  onClose,
  onPick,
  onCreate,
}) {
  const { theme } = useTheme();

  return (
    <BottomSheet
      open={!!isOpen}
      onClose={onClose}
      label="Pick a chase binder"
      overlayStyle={{ ...styles.overlay, backgroundColor: theme.overlay }}
      sheetStyle={{ ...styles.sheet, backgroundColor: theme.bgSheet }}
    >
      <div style={styles.handle} />
      <div style={styles.header}>
        <h2 style={{ ...styles.title, color: theme.text }}>Which chase binder?</h2>
        <button
          style={{ ...styles.closeBtn, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary }}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {binders.length === 0 && (
        <p style={{ ...styles.empty, color: theme.textMuted }}>
          No chase binders yet. Make one and you can start filling it.
        </p>
      )}

      {binders.map((b) => (
        <button
          key={b.id}
          style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border }}
          onClick={() => { haptics.tap(); onPick?.(b); }}
        >
          <Folder size={18} color={theme.accent} />
          <span style={{ ...styles.rowName, color: theme.text }}>{b.name}</span>
          <span style={{ ...styles.rowMeta, color: theme.textMuted }}>
            {counts[b.id] || 0} {counts[b.id] === 1 ? 'card' : 'cards'}
          </span>
        </button>
      ))}

      <button
        style={{ ...styles.row, backgroundColor: theme.bgCard, borderColor: theme.border }}
        onClick={() => { haptics.tap(); onCreate?.(); }}
      >
        <FolderPlus size={18} color={theme.accent} />
        <span style={{ ...styles.rowName, color: theme.text }}>Create a chase binder</span>
      </button>
    </BottomSheet>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10002 },
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    borderRadius: '20px 20px 0 0',
    padding: '12px 20px calc(24px + var(--safe-area-bottom, 0px))',
    zIndex: 10003,
    maxHeight: '70vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
  },
  handle: { width: '36px', height: '4px', backgroundColor: '#3a3a3a', borderRadius: '2px', margin: '0 auto 12px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '4px' },
  title: { fontSize: '17px', fontWeight: '600', margin: 0 },
  closeBtn: {
    width: '30px', height: '30px', borderRadius: '8px', border: '1px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer', outline: 'none', flexShrink: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  empty: { fontSize: '13px', margin: '4px 0', lineHeight: 1.4 },
  row: {
    display: 'flex', alignItems: 'center', gap: '12px', minHeight: '52px', padding: '0 14px',
    borderRadius: '12px', border: '1px solid', cursor: 'pointer', textAlign: 'left',
    outline: 'none', WebkitTapHighlightColor: 'transparent',
  },
  rowName: { flex: 1, fontSize: '15px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta: { fontSize: '12px', flexShrink: 0 },
};
